/**
 * Client Care role in recruitment. Client-safe: no DB access.
 *
 * A candidate is a Client Care candidate when the position they applied for is
 * a Client Care position (the Philippines funnel offers "Client Care Associate
 * JR (PH)" next to the agent position). For those candidates the Client Care
 * coordinators below can be picked as the interviewer, on top of the HR Stage 1
 * interviewers, and HR creates the employee straight on the Client Care
 * position instead of a Training position.
 *
 * The Client Care interviewers only see the candidates whose interview is
 * assigned to them: since 2026-10-19 that is the general
 * `recruitment.view_all_candidates` switch (off) in recruitment-permissions.ts;
 * their per-user grants still carry the legacy `client_care_interviewer` flag,
 * which resolves to the same scope. Super Admin is never scoped.
 */
import { PH_DEFAULT_POSITION, isPhilippinesCandidate } from '@/lib/recruitment/international';
import { isPhilippinesPositionName } from '@/lib/philippines-payroll';
import { REVO_QA_PH_APPLICATION_POSITION } from '@/lib/recruitment/revo-qa-recruitment';
import {
  employeeMatchesInterviewerEntries,
  nameMatchesInterviewerEntries,
} from '@/lib/recruitment/interviewer-names';
import {
  LEGACY_SCOPED_INTERVIEWER_KEY,
  isAssignedInterviewerScope,
} from '@/lib/recruitment/recruitment-permissions';

export const CLIENT_CARE_PH_POSITION = 'Client Care Associate JR (PH)';

/**
 * Positions HR can accept a Philippines application for (Accept modal, and
 * the quick picks of the Recruit page's Philippines candidate form). Revo QA
 * (Quality Assurance (PH)) since 2026-10-13: hired straight on that position
 * under Revo, no Ucallz / Teli training class (revo-qa-recruitment.ts).
 */
export const PH_APPLICATION_POSITIONS = [
  { value: PH_DEFAULT_POSITION, role: 'Agent' },
  { value: CLIENT_CARE_PH_POSITION, role: 'Client Care' },
  REVO_QA_PH_APPLICATION_POSITION,
] as const;

/** Positions HR can accept a Client Care application for (first = default). */
export const CLIENT_CARE_APPLICATION_POSITIONS = [
  { value: CLIENT_CARE_PH_POSITION, role: 'Client Care' },
  { value: PH_DEFAULT_POSITION, role: 'Agent' },
  REVO_QA_PH_APPLICATION_POSITION,
] as const;

export function isClientCarePositionName(name: string | null | undefined): boolean {
  return /client\s*care/i.test(name ?? '');
}

export function isClientCareCandidate(
  candidate: { position_applied?: string | null } | null | undefined,
): boolean {
  return isClientCarePositionName(candidate?.position_applied);
}

/**
 * Client Care positions a Client Care candidate can be hired on (Create
 * Employee), the one they applied for first: Philippines candidates get the
 * "(PH)" Client Care positions, everyone else the Mexico ones.
 */
export function clientCareHirePositions<T extends { name: string }>(
  positions: T[],
  candidate: { position_applied?: string | null; country?: string | null } | null | undefined,
): T[] {
  if (!isClientCareCandidate(candidate)) return [];
  const applied = candidate?.position_applied?.trim().toLowerCase() ?? '';
  const philippines = isPhilippinesCandidate(candidate);
  const isApplied = (p: T) => (p.name.trim().toLowerCase() === applied ? 1 : 0);
  return positions
    .filter((p) => isClientCarePositionName(p.name) && isPhilippinesPositionName(p.name) === philippines)
    .sort((a, b) => isApplied(b) - isApplied(a) || a.name.localeCompare(b.name));
}

/**
 * Client Care interviewers as "First name + first surname" (matching rules in
 * interviewer-names.ts: first name + first surname, accents and case ignored).
 */
export const CLIENT_CARE_INTERVIEWERS = [
  'Martin Moreno',
  'Gilberto Hernandez',
  'Mariana Santana',
] as const;

/** Employee record (first_name / last_name) is one of the Client Care interviewers. */
export function isClientCareInterviewerEmployee(employee: {
  first_name?: string | null;
  last_name?: string | null;
}): boolean {
  return employeeMatchesInterviewerEntries(CLIENT_CARE_INTERVIEWERS, employee);
}

/** Full interviewer name as stored on the interview ("Martin Moreno Madriz"). */
export function isClientCareInterviewerName(name: string | null | undefined): boolean {
  return nameMatchesInterviewerEntries(CLIENT_CARE_INTERVIEWERS, name);
}

/**
 * Legacy recruitment sub-permission (2026-09-28) that scoped a user to their
 * own Client Care interviews. Since 2026-10-19 the scope is the general
 * `view_all_candidates` switch (recruitment-permissions.ts): off = only the
 * candidates with an interview assigned to the user. Grants still carrying
 * this flag resolve to view_all_candidates = false.
 */
export const CLIENT_CARE_INTERVIEWER_PERMISSION = LEGACY_SCOPED_INTERVIEWER_KEY;

/** @deprecated use isAssignedInterviewerScope (recruitment-permissions.ts). */
export const isClientCareInterviewerScope = isAssignedInterviewerScope;
